import { useContext, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { PolymorphicChart } from "@foodpilot/foods";
import type { ChartType } from "@foodpilot/foods";
import { t } from "i18next";
import i18next from "i18next";

import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useSsqIdForScore } from "./useSsqIdForScore";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import { useGetOneScore } from "src/api/sure-api/score/useGetOneScore";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { OlapLocalizedLabelService } from "src/context/OlapScores/OlapLocalizedLabelService";
import { OlapHeadingColorService } from "src/context/OlapScores/OlapHeadingColorService";
import { resolveChartType } from "src/utils/chart";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { formatNumber } from "src/utils/formatting";
import type { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import type { Dimensions } from "src/context/OlapScores/useDimensions";
import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard";
import { isAppInPDFMode } from "src/utils/rendering";
import { RootPropsContext } from "src/components/Puck/config/root";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export type ScoreChartblockProps = {
    scoreId?: number | null;
    variationId?: number | null;
    ssqId?: number | null;
    showTitle?: boolean;
    showUnitSubtitle?: boolean;
    title?: string;
    chartType?: ChartType | "auto";
    showLegend?: boolean;
    legendPosition?: "bottom" | "right";
    showTooltip?: boolean;
    spacing?: number;
    borderWidth?: number;
};

export const ScoreChartblock = (props: ScoreChartblockProps) => {
    const {
        scoreId,
        variationId,
        showTitle = true,
        showUnitSubtitle = false,
        title,
        chartType = "auto",
        showLegend = false,
        legendPosition = "bottom",
        showTooltip = true,
        spacing = 2,
        borderWidth = 2,
    } = props;
    const theme = useTheme();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const rootProps = useContext(RootPropsContext);
    const isPdfMode = rootProps?.pageType === "PDF" || isAppInPDFMode();

    const scoreKey = useMemo(() => (scoreId ? String(Number(scoreId)) : null), [scoreId]);

    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;
    const campaignId = currentCampaign?.id ? Number(currentCampaign.id) : undefined;
    const ssqId = useSsqIdForScore(Number(scoreId || 0));
    const { data: ssq } = useGetOneSsq(ssqId);

    const { score: scoreMeta } = useGetOneScore(Number(scoreId || 0));

    const scoresQuery = useMemo<OlapScoresQuery | undefined>(() => {
        if (!scoreId) return undefined;

        const dimensions: Dimensions = [[["headings"]]];

        return {
            scores: {
                ids: [Number(scoreId)],
                dimensions,
                variations:
                    variationId !== null && variationId !== undefined ?
                        { [Number(scoreId)]: Number(variationId) }
                    :   undefined,
            },
        };
    }, [scoreId, variationId]);

    const { post: companyPost, isLoading } = useGetOrganizationPost({
        companyId,
        ssqId,
        campaignId,
        scoresQuery,
        enabled: Boolean(companyId && ssqId && campaignId && scoreId),
    });
    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(companyPost?.ssqVersionId);

    const { entries, values, labels, unit, total } = useMemo(() => {
        if (!scoreId || !scoreKey || !companyPost?.olapScores?.[scoreKey]) {
            return {
                entries: [] as Array<{ id: string; label: string; value: number }>,
                values: [] as number[],
                labels: [] as string[],
                unit: "",
                total: null as number | null,
            };
        }

        const mainScore = companyPost.olapScores[scoreKey];
        const headings = OlapScoresService.getDimension("headings", mainScore.dimensions);
        const flattened = OlapScoresService.flattenDimensionValues(headings);

        const entries = flattened.flatMap((d) => {
            const v = OlapScoresService.normalizeNumericalScore(d.score, null);
            if (v === null) return [];
            const localized = OlapLocalizedLabelService.localize("headings", d.id, { ssqVersion }) ?? d.label;
            return [{ id: d.id, label: localized, value: v }];
        });

        return {
            entries,
            values: entries.map((e) => e.value),
            labels: entries.map((e) => e.label),
            unit: mainScore.unit || "",
            total: OlapScoresService.normalizeNumericalScore(mainScore.score.value, null),
        };
    }, [companyPost?.olapScores, scoreId, scoreKey, ssq]);

    const chartTypeValue: ChartType = useMemo(() => {
        if (chartType !== "auto") return chartType;
        const dt = scoreMeta?.datavizType;
        return resolveChartType(dt, "donut");
    }, [chartType, scoreMeta?.datavizType]);

    const displayTitle = useMemo(() => {
        if (!showTitle) return null;
        const trimmed = (title ?? "").trim();
        if (trimmed) return trimmed;
        return scoreMeta?.title ?? null;
    }, [scoreMeta?.title, showTitle, title]);

    const { chartValues, chartLabels, chartEntries, totalDenominator } = useMemo(() => {
        if (chartTypeValue !== "cumulative") {
            return {
                chartValues: values,
                chartLabels: labels,
                chartEntries: entries,
                totalDenominator: total,
            };
        }

        const sorted = [...entries].sort((a, b) => b.value - a.value);
        const sum = sorted.reduce((acc, e) => acc + (Number.isFinite(e.value) ? e.value : 0), 0);

        const denom = typeof total === "number" && Number.isFinite(total) && total > 0 ? total : sum;

        const percentValues =
            denom > 0 ? sorted.map((e) => Math.round((e.value / denom) * 10000) / 100) : sorted.map(() => 0);

        return {
            chartValues: percentValues,
            chartLabels: sorted.map((e) => e.label),
            chartEntries: sorted,
            totalDenominator: denom,
        };
    }, [chartTypeValue, entries, labels, total, values]);

    const chartColors = useMemo(
        () =>
            OlapHeadingColorService.resolveMany(
                chartEntries.map((entry) => ({ dimension: "headings", id: entry.id })),
                { ssqVersion },
            ),
        [chartEntries, ssqVersion],
    );

    const isEmpty = !isLoading && (chartValues.length === 0 || chartLabels.length === 0);

    if (!scoreId || isEmpty || !isSsqVersionReady) {
        return (
            <Box
                sx={{
                    width: "100%",

                    display: "flex",
                }}
            >
                <EmptyStateCard
                    title={t("cms.score.nodata.title", "Unable to display chart")}
                    description={t("cms.score.nodata.description", "Data not available for this score")}
                />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    width: "100%",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.spacing(1),
                    backgroundColor: theme.palette.background.paper,
                    px: theme.spacing(2),
                    pb: theme.spacing(2),
                    pt: theme.spacing(4),
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.spacing(1),
                    overflow: "hidden",
                    "& .doughnut-chart-content": {
                        padding: "unset",
                    },
                }}
            >
                {displayTitle && (
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                        <Typography variant="h4" align="center">
                            {displayTitle}
                        </Typography>

                        {showUnitSubtitle && unit.trim() !== "" && (
                            <Typography
                                variant="caption"
                                align="center"
                                sx={{ display: "block", color: theme.custom.grey[1300] }}
                            >
                                {unit}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flex: 1,
                    }}
                >
                    <PolymorphicChart
                        className="score-chartblock-polymorphic"
                        chartType={chartTypeValue}
                        values={chartValues}
                        labels={chartLabels}
                        colors={chartColors}
                        createPalette={false}
                        isLoading={isLoading}
                        width="100%"
                        displayTotal={
                            totalDenominator !== null && totalDenominator !== undefined ?
                                formatNumber(totalDenominator, i18next.language)
                            :   undefined
                        }
                        valuesUnit={unit}
                        legend={showLegend ? { position: legendPosition } : undefined}
                        valuesFormatterFn={(value: number) => formatNumber(value, i18next.language)}
                        spacing={spacing}
                        borderWidth={borderWidth}
                        showTooltip={showTooltip}
                        aggregation={{
                            showAggregateToggle: !isPdfMode && chartValues.length > 1,
                            aggregateToggleLabel: t(
                                "components.chart.aggregate_toggle_label",
                                "Group scores with less than 1%",
                            ),
                            othersCategoryLabel: t("components.chart.others_category_label", "Others"),
                            othersThresholdPercent: 1,
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ScoreChartblock;
